/**
 * useInquiryFiles フック
 *
 * 問い合わせに紐づくファイル一覧を取得するカスタムフック
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { inquiryFilesRepository, type FileType, type InquiryFile } from '../data/inquiryFilesRepository';

interface UseInquiryFilesResult {
  files: InquiryFile[];
  fileUrlMap: Record<string, string>;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

/**
 * 問い合わせファイル取得フック
 * @param inquiryId 問い合わせID
 * @param type ファイル種別
 * @param token 認証トークン
 */
export const useInquiryFiles = (
  inquiryId: string,
  type: FileType,
  token?: string
): UseInquiryFilesResult => {
  const [files, setFiles] = useState<InquiryFile[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // 前回のinquiryIdを保持（同期的な変化検知用）
  const prevInquiryIdRef = useRef<string>(inquiryId);

  // inquiryIdが変わったかを同期的にチェック
  // useEffectは非同期なので、レンダリング中に状態を正しく返すために必要
  const inquiryIdChanged = prevInquiryIdRef.current !== inquiryId;
  if (inquiryIdChanged) {
    prevInquiryIdRef.current = inquiryId;
  }

  // inquiryIdが空から有効値に変わった場合、このレンダリングではローディング中とみなす
  // （useEffectでfetchFilesが呼ばれる前の状態）
  const isPendingFetch = inquiryIdChanged && inquiryId !== '';

  const fetchFiles = useCallback(async () => {
    if (!inquiryId) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    const result = await inquiryFilesRepository.getFiles(inquiryId, type, token);

    if (result.success) {
      setFiles(result.data);
    } else {
      setError(result.error.message);
    }

    setIsLoading(false);
  }, [inquiryId, type, token]);

  useEffect(() => {
    fetchFiles();
  }, [fetchFiles]);

  // ファイル名をキーにしたURLマップを作成
  const fileUrlMap = files.reduce<Record<string, string>>((acc, file) => {
    acc[file.fileName] = file.url;
    return acc;
  }, {});

  return {
    files,
    fileUrlMap,
    // isPendingFetchの場合は、useEffectでfetchが始まる前だがローディング中とみなす
    isLoading: isLoading || isPendingFetch,
    error,
    refetch: fetchFiles,
  };
};
